import { Component, NgZone, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from '@angular/material/dialog';
import { InfoDialogComponent } from "../info-dialog/info-dialog.component";
import { compare } from "semver";
import {Bmi} from "../../model/Bmi";
import {UserIdentity} from "../../model/UserIdentity";
import {Constants} from "../../utils/constants";
import {FileManagerProvider} from "../../providers/file-manager/file-manager";
import {ActivatedRoute, Router} from "@angular/router";
import {WebSurveyProvider} from "../../providers/web-service/webservice-survey";
import {NgxSpinnerService} from "ngx-spinner";
import {TranslateService} from "@ngx-translate/core";
import {TranslationProvider} from "../../providers/translation/translation";
import {AnswerSaveActions, ResponseType} from "../../utils/enums";
import {SurveyResponseWrapper} from "../../model/SurveyResponseWrapper";
import {SurveyResponse} from "../../model/SurveyResponse";
import {SurveyQuestion} from "../../model/SurveyQuestion";
import {SurveySource} from "../../model/SurveySource";
import {SurveyEnumAnswer} from "../../model/SurveyEnumAnswer";
import {SurveyStringAnswer} from "../../model/SurveyStringAnswer";
import {SurveyAnswer} from "../../model/SurveyAnswer";
import {SurveyNumberAnswer} from "../../model/SurveyNumberAnswer";
import {SurveyDisplayEvent} from "../../model/analytics/SurveyDisplayEvent";
import {SurveyContentEvent} from "../../model/analytics/SurveyContentEvent";
import {SurveyAnalyticsEvent} from "src/model/analytics/SurveyAnalyticsEvent";
import { environment } from "../../environments/environment";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

// import the javascript functions to allow typescript invocation
declare var postSurveyResponse;
declare var declineSurvey;
declare var storeSurveyResponse;
declare var isJavascriptInjectedIntoWebView;

// mask sense functions
declare var startImageCapture;

// @ts-ignore
@Component({
  selector: "app-survey-base",
  host: {
    '(window:image-capture-completed)':'imageCaptureCompleted($event, response)',
    '(window:image-capture-interrupted)':'imageCaptureInterrupted($event, response)'
  },
  templateUrl: "./survey-base.component.html",
  styleUrls: ["./survey-base.component.scss"],
  standalone: false
})
export class SurveyBaseComponent implements OnInit, OnDestroy {
  invite: string;
  darkMode: boolean = false;
  patientId: string;
  surveyTemplate: any = null;
  surveyData: any = null;
  surveyMethod: string = null;
  serial: string = null;
  environmentConfig: any = null;
  patientBmi: Bmi = new Bmi();
  identity: UserIdentity = null;
  valid = true;
  showHeader = false;
  language: string = null;
  currentQuestion: any;
  currentQuestionIndex = 0;
  selectedAnswer: any;
  type: string = "smart-coaching";
  continueText: string;
  cancelText: string;
  selectedTexts: string[] = [];
  useWebView = false;
  appVersion = null;
  recentDeclined = false;
  usePush = false;
  showSurveyQuestions = false;
  showCancelButton = false;
  showBackButton = true;
  showHeaderInfoButton = false;
  showDialog = false;
  dialogObj: any = {};
  currentDialog: string = "FirstDialog";
  progressBarStyle: string;
  ariaValueNow: string;
  validationTextStringId = "please-select-option";
  skipTextStringId = "skip";
  sleepConciergeSurveyId = "sleep-concierge-v1";
  airCurve11SleepinessSurveyId = "sleep-quality-v1";
  onboardingSurveyId = "subjective-feedback-v1";
  useYourDeviceAS11SleepLibraryId = 65;
  adjustRampTimeAS11SleepLibraryId = 59;
  scheduleStartDateForExperiencedUsers = 208;
  getUsedToTherapyAnswerId = "getting-used-to-therapy";
  breathOptions = ["breath-in", "breath-out"];
  baselineQuestionId = "how-sleepy-prior-to-therapy";
  selfHelpFollowUpQuestionId = "did-content-solve-issue";
  repapSurveyId = "repap-v1";
  defaultContinueText: string = "continue";
  surveyDataPath = environment.useMockData ? "assets/mock-data/sleep-concierge-v1-issue-expansion.json" : `${window.location.origin}/survey-api/mqui/metadata`;
  surveyAnalyticsEvents: SurveyAnalyticsEvent[] = [];
  savedResponses: SurveyResponse = null;
  exitingSurvey: boolean = false;
  allowSkip: boolean = false;
  footer: string = "";
  repapContentStringId = "repap-v1-hm1-info";
  dialogTemplateTypes = ["Dialog", "TherapyRecommendation"];
  submittedCodeRequest: boolean = false;
  sanitizedWarningText: SafeHtml;

  surveyAnswerCoachingLibraryActionMapper = {
    "sleep-library": Constants.ExitToSleepLibraryAction,
    "dashboard": Constants.ExitToDashboardAction,
    "device": Constants.ExitToCoachingLibraryEquipmentAction,
    "mask": Constants.ExitToCoachingPTAMaskSetupAction,
    "pressure": Constants.ExitToCoachingPTATestDriveAction,
    "breath-in": Constants.ExitToCoachingLibraryEquipmentAction,
    "breath-out": Constants.ExitToCoachingLibraryEquipmentAction,
    "symptoms": Constants.ExitToSleepLibraryAction,
    "other": Constants.ExitToSleepLibraryAction
  };

  microCoachingMapper = {
    extremely: {
      extremely: "ongoing-follow-up-patient-story-1-insight",
      very: "ongoing-follow-up-patient-story-1-insight",
      moderately: "amazing",
      slightly: "amazing",
      notatall: "amazing"
    },
    very: {
      extremely: "ongoing-follow-up-patient-story-2-insight",
      very: "ongoing-follow-up-patient-story-1-insight",
      moderately: "amazing",
      slightly: "amazing",
      notatall: "amazing"
    },
    moderately: {
      extremely: "ongoing-follow-up-patient-story-2-insight",
      very: "ongoing-follow-up-patient-story-2-insight",
      moderately: "ongoing-follow-up-patient-story-1-insight",
      slightly: "amazing",
      notatall: "amazing"
    },
    slightly: {
      extremely: "ongoing-follow-up-patient-story-2-insight",
      very: "ongoing-follow-up-patient-story-2-insight",
      moderately: "ongoing-follow-up-patient-story-2-insight",
      slightly: "default-insight",
      notatall: "default-insight"
    },
    notatall: {
      extremely: "ongoing-follow-up-patient-story-2-insight",
      very: "ongoing-follow-up-patient-story-2-insight",
      moderately: "ongoing-follow-up-patient-story-2-insight",
      slightly: "default-insight",
      notatall: "default-insight"
    },
    default: {
      extremely: "ongoing-follow-up-patient-story-2-insight",
      very: "ongoing-follow-up-patient-story-2-insight",
      moderately: "ongoing-follow-up-patient-story-2-insight",
      slightly: "default-insight",
      notatall: "default-insight"
    },
    defaultsleepquality: {
      verypoor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      poor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      ok: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      good: "sleep-quality-v1-insight-default",
      excellent: "sleep-quality-v1-insight-default"
    },
    verypoor: {
      verypoor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      poor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      ok: "sleep-quality-v1-insight-amazing",
      good: "sleep-quality-v1-insight-amazing",
      excellent: "sleep-quality-v1-insight-amazing"
    },
    poor: {
      verypoor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      poor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      ok: "sleep-quality-v1-insight-amazing",
      good: "sleep-quality-v1-insight-amazing",
      excellent: "sleep-quality-v1-insight-amazing"
    },
    ok: {
      verypoor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      poor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      ok: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      good: "sleep-quality-v1-insight-amazing",
      excellent: "sleep-quality-v1-insight-amazing"
    },
    good: {
      verypoor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      poor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      ok: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      good: "sleep-quality-v1-insight-default",
      excellent: "sleep-quality-v1-insight-default"
    },
    excellent: {
      verypoor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      poor: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      ok: "sleep-quality-v1-insight-ongoing-follow-up-patient-story-1",
      good: "sleep-quality-v1-insight-default",
      excellent: "sleep-quality-v1-insight-default"
    },
  };
  selfHelpFollowUpDecriptionMapper = {
    "sleep-concierge-v1": {
      "breath-in": "sleep-concierge-v1:selfHelpFollowUp:breath:pushDescription",
      "breath-out": "sleep-concierge-v1:selfHelpFollowUp:breath:pushDescription",
      "device": "sleep-concierge-v1:selfHelpFollowUp:device:pushDescription",
      "mask": "sleep-concierge-v1:selfHelpFollowUp:mask:pushDescription",
      "pressure": "sleep-quality-v1-self-help-follow-up-pressure",
      "symptoms": "sleep-concierge-v1-self-help-follow-up-symptoms",
    },
    "sleep-quality-v1": {
      "device": "sleep-quality-v1-self-help-follow-up-device",
      "mask": "sleep-quality-v1-self-help-follow-up-mask",
      "pressure": "sleep-quality-v1-self-help-follow-up-pressure",
      "symptoms": "sleep-quality-v1-self-help-follow-up-symptoms",
    },
    "niv-checkin-v1": {
      "machine": "niv-checkin-v1-self-help-follow-up-machine",
      "mask": "niv-checkin-v1-self-help-follow-up-mask",
      "pressure": "niv-checkin-v1-self-help-follow-up-pressure",
      "symptoms": "niv-checkin-v1-self-help-follow-up-symptoms",
    }
  }
  imageCaptureFlowIds = ["maskSense"];
  maskSenseFlowId = "maskSense";
  repapGroupStringIdMapping = {
    "repap-hme1": "repap-v1-hm1-info",
    "repap-hme2": "repap-v1-hm2-info",
    "repap-hme3": "repap-v1-hm3-info"
  }

  constructor(
    protected route: ActivatedRoute,
    private router: Router,
    protected fileManager: FileManagerProvider,
    protected webInterface: WebSurveyProvider,
    private spinner: NgxSpinnerService,
    private translate: TranslateService,
    private localeMapper: TranslationProvider,
    private headerInfoDialog: MatDialog,
    private _ngZone: NgZone,
    private sanitizer: DomSanitizer
  ) {
    window['surveyRef'] = {component: this, zone: _ngZone};
  }

  ngOnInit(): void {
    this.spinner.show();
    this.readRouteParams();
    this.loadSurvey();
  }

  ngOnDestroy(): void {

  }

  readRouteParams(): void {
    this.invite = this.readFromQueryString("invite");
    this.patientId = this.readFromQueryString("patientId");
    this.language = this.readFromQueryString("language", false);
    this.serial = this.readFromQueryString("serial", false);
    this.useWebView = this.readFromQueryString("appVersion", false) !== null;
    this.appVersion = this.readFromQueryString("appVersion", false) ?? "1.0.0";
    this.usePush = this.readFromQueryString("push", false) !== null;
    this.recentDeclined = this.readFromQueryString("decline", false) !== null;
    this.showHeader = this.useWebView || this.usePush;
    this.darkMode = this.readFromQueryString("darkMode", false) === "true";
  }

  /**
   * Read the identity fields so we know
   * who the user is
   */
  readIdentity(): void {
    this.identity = new UserIdentity(this.getDeviceSerial(), this.surveyData.patientId, this.surveyData.sourceIdType);
  }

  /**
   * Sets the language based on the param passed in the query string
   */
  setLanguage(): void {
    if (this.language === null) {
      return;
    }

    const locale = this.localeMapper.getLocale(this.language);

    if (locale !== null) {
      this.language = locale;
      this.translate.use(locale);
    }
  }

  /**
   * Reads a value from the query string.
   * @param paramName The name of the querystring parameter.
   * @param required (Optional) Whether the param is required. Defaults
   * to true.
   */
  readFromQueryString(paramName: string, required: boolean = true): string {
    const value = this.route.snapshot.queryParamMap.get(paramName);

    if (required && !value) {
      console.error(`Error: Required querystring key "${paramName}" missing from request URL`);
      this.redirectToNotFound();
    }

    return value;
  }

  checkMinimumVersion(minimum_version, current_version): boolean{
    let result = true;

    // Very old apps don't even send us their version.
    if ((current_version ?? "") === ""){
      result = false;
    }

    if ((minimum_version ?? "") !== "" && (current_version ?? "") !== "") {
      result = compare(current_version, minimum_version) >= 0;
    }
    return result;
  }

  /**
   * Loads the survey definition based on data passed into the app
   * display to the user.
   */
  loadSurvey(): void {
    let url = `${this.surveyDataPath}?invite=${this.invite}&patientId=${this.patientId}`;
    this.fileManager.getJSONDataAsync(encodeURI(url)).then(data => {
      if (!this.checkMinimumVersion(data.surveyTemplate?.scope?.minimum_myair_version, this.appVersion)){
        console.error(`Error: Survey has minimum version ${data.surveyTemplate?.scope?.minimum_myair_version}, app version is ${this.appVersion}.  Clearing notification and quiting survey.`);
        this.clearAppSyncNotificationAndExit();
        return;
      }
      this.environmentConfig = data.environmentVariables;
      this.localeMapper.updateEnvironmentLanguages(this.environmentConfig.defaultLocale, this.environmentConfig.supportedLocales);
      this.setLanguage();
      this.surveyTemplate = data.surveyTemplate;
      this.surveyData = data.surveyData;
      this.surveyMethod = data.surveyMethod;
      this.readIdentity();

      this.showHeaderInfoButton = this.surveyTemplate.hasOwnProperty("InfoDialog");

      // If a survey is opened by a push notification, it goes to questions directly with the exception of self-help follow-up and templated cases.
      if (this.dialogTemplateTypes.indexOf(this.surveyTemplate?.Template) > -1) {
        this.showDialog = true;
        this.setupDialog();
      }
      else if (this.surveyTemplate.hasOwnProperty("EngagementCard")) {
        this.convertEngagmentCardToDialog();
        this.showDialog = true;
        this.setupDialog();
      }
      else {
        this.setupSurveyQuestions();
      }
    })
      .catch(error => {
        console.error(`Error loading survey content: ${error}`);
        window.location.href = Constants.ExitToDashboardAction;
      })
      .finally(() => {
        if (!this.exitingSurvey){
          this.spinner.hide();
        }
      });
  }

  answerSelected(answer: any): void {
    this.resetSelection();
    if (this.currentQuestion.Template === "ButtonMultiGroup") {
      if (!answer && (this.currentQuestion.RequiresAnswer ?? true)){
        this.validationTextStringId = "please-select-option";
        this.valid = false;
        this.selectedAnswer = null;
        return;
      }
      else if (!answer) {
        this.valid = true;
        return;
      }
    }
    if (!answer && (this.currentQuestion.RequiresAnswer ?? true)) {
      this.validationTextStringId = this.currentQuestion.Template !== "BMI" ? "please-select-option" : "input-all-fields";
      this.valid = false;
      this.selectedAnswer = null;
      return;
    }
    else if (!answer) {
      this.valid = true;
      this.selectedAnswer = null;
      this.surveyTemplate.Questions[this.currentQuestionIndex].SelectedAnswer = answer;
      this.surveyTemplate.Questions[this.currentQuestionIndex].declined = false;
      this.updatePageText();
      return;
    }

    if (answer.FreeText && answer.RequiresText && (answer.Description?.trim() === "" || answer.Description === undefined)) {
      this.validationTextStringId = "enter-description";
      this.selectedAnswer = answer;
      this.updatePageText();
      this.selectedAnswer = null;
      return;
    }

    this.valid = true;
    this.selectedAnswer = answer;
    this.surveyTemplate.Questions[this.currentQuestionIndex].SelectedAnswer = answer;
    this.updatePageText();
  }

  getFirstVisibleQuestion(): any{
    return this.surveyTemplate.Questions.find(x => x.isFirstQuestion);
  }

  getLastVisibleQuestion(): any {
    return this.surveyTemplate.Questions.findLast(x => x.isLastQuestion);
  }

  updatePageText(): void {
    this.refreshVisibilityBasedOnResponseScope();
    this.selectedTexts = this.getAnswerSelectedText(this.selectedAnswer);

    // Check if this selected text requires a reference.
    if (this.currentQuestion.hasOwnProperty("MicroCoachingReferenceLookup") && this.selectedTexts.length > 0 &&
        this.currentQuestion["MicroCoachingReferenceLookup"].hasOwnProperty(this.selectedTexts[0])) {
          this.selectedAnswer["AnswerReference"] = this.currentQuestion.MicroCoachingReferenceLookup[this.selectedTexts[0]]["AnswerReference"];
    }

    const isFirstQuestion = this.currentQuestion?.QuestionId === this.getFirstVisibleQuestion()?.QuestionId;
    const isLastQuestion = this.currentQuestion?.QuestionId === this.getLastVisibleQuestion()?.QuestionId;

    let questionContinueText = this.selectedAnswer?.ContinueText ?? this.currentQuestion.DefaultContinueText ?? this.defaultContinueText;
    // If this is the last question, but isn't a special action, and is in a valid state use standard "done"
    const validDoneState = !(this.isAnswerSelected() === false && (this.currentQuestion.RequiresAnswer ?? true) !== false)
    if (isLastQuestion && validDoneState && this.currentQuestion.Template !== "SurveyCompletedDialog" &&
          !([AnswerSaveActions[AnswerSaveActions.SleepLibrary], AnswerSaveActions[AnswerSaveActions.ArticleDisplay], AnswerSaveActions[AnswerSaveActions.MaskWizard]].includes(this.selectedAnswer?.Action))) {
      questionContinueText = "done";
    }
    this.continueText = questionContinueText;

    let questionCancelText = this.selectedAnswer?.CancelText ?? this.getDefaultCancelText();
    if (isFirstQuestion && questionCancelText == "back")  {
      questionCancelText = "";
    }
    this.showBackButton = !(isFirstQuestion || questionCancelText == "back" || this.currentQuestion["DisableBackNavigation"]);
    this.cancelText = this.showCancelButton ? questionCancelText : "";

    this.setProgressBar();
  }

  getAnswerSelectedText(answer: any): string[] {
    if (answer?.SelectedText === "conditional-microcoaching") {
      return [this.getMicroCoachingStringId(answer)];
    }
    if (Array.isArray(answer?.SelectedText)) {
      return answer.SelectedText;
    }

    return [answer?.SelectedText];
  }

  clearAppSyncNotificationAndExit(): void {
    this.exitingSurvey = true
    this.spinner.show();
    let successCallback = () => {};
    let failedCallback = () => {};

    if (!isJavascriptInjectedIntoWebView()){
      successCallback = () => {
        this.selectedAnswer = null;
        // trigger to notify the app that the session is ended.
        window.location.href = Constants.ExitToDashboardAction;
      };
      failedCallback = () => {
        window.location.href = Constants.ExitToDashboardAction;
      };
    }

    postSurveyResponse(null, Constants.ExitToDashboardAction).then(successCallback)
      .catch(failedCallback);
  }

  continueSelected(): void {
    if (this.showDialog){
      this.sanitizedWarningText = null;
      this.handleDialogPositiveAction();
    }
    else if (this.showSurveyQuestions) {
      if (this.isAnswerSelected() === false && (this.currentQuestion.RequiresAnswer ?? true) === false) {
        this.answerSelected(null);
      }
      this.handleQuestionContinueAction();
    }
  }

  cancelSelected(): void {
    let currentQuestion = this.surveyTemplate.Questions?.[this.currentQuestionIndex];
    if(currentQuestion?.Template === 'SurveyCompletedDialog') {
      this.submitAnswerAndQuit(Constants.ExitToDashboardAction);
      return;
    }

    if (this.showDialog) {
      this.handleDialogNegativeAction();
    }
    else {
      this.handleQuestionNegativeAction();
    }
  }

  async handleDialogPositiveAction(): Promise<void> {
    let submittable = this.dialogObj?.QuestionId != null;
    let navigateTo = this.dialogObj.PositiveButton.NavigateTo;

    if (navigateTo?.startsWith("https://")) {
      await this.openExternalLinkAfterDialogSubmit(this.dialogObj.PositiveButton.AnswerId, navigateTo, submittable);
      return;
    }

    if (submittable) {
      await this.submitDialogSurveyResponse(this.dialogObj.PositiveButton.AnswerId)
        .catch(err => this.handleException(err));
    }
    if (navigateTo in this.surveyAnswerCoachingLibraryActionMapper){
      this.navigate(this.surveyAnswerCoachingLibraryActionMapper[navigateTo]);
    }
    else if (navigateTo === "backToQuestions") {
      this.currentQuestion = this.surveyTemplate.Questions[this.currentQuestionIndex];
      this.showDialog = false;
      this.nextQuestion();
    }
    else{
      this.currentDialog = navigateTo;
      this.setupDialog();
    }
  }

  async handleDialogNegativeAction(): Promise<void> {
    let submittable = this.dialogObj.QuestionId != null;
    let navigateTo = this.dialogObj.NegativeButton.NavigateTo;

    if (navigateTo in this.surveyAnswerCoachingLibraryActionMapper){
      // Exit/close targets (e.g. "dashboard"): post the response with the
      // resolved action as the redirect so the native surveyPosted callback
      // navigates only AFTER the post (and notification clear) completes.
      // Navigating first (the Android race) cancels the in-flight post and the
      // survey notification re-prompts.
      await this.submitDialogAndNavigate(
        this.dialogObj.NegativeButton.AnswerId,
        this.surveyAnswerCoachingLibraryActionMapper[navigateTo],
        submittable,
      );
      return;
    }

    if (submittable) {
      await this.submitDialogSurveyResponse(this.dialogObj.NegativeButton.AnswerId)
        .catch(err => this.handleException(err));
    }
    if (navigateTo === "backToQuestions") {
      this.currentQuestion = this.surveyTemplate.Questions[this.currentQuestionIndex];
      this.showDialog = false;
      this.nextQuestion();
    }
    else{
      this.currentDialog = navigateTo;
      this.setupDialog();
    }
  }

  handleQuestionContinueAction(): void {
    let currentQuestion = this.surveyTemplate.Questions[this.currentQuestionIndex];
    if (currentQuestion.Template === 'PreQuestionDialog') {
      this.nextQuestion();
      return;
    }

    let action = this.determineAction(currentQuestion);

    // Currently submitting one answer at a time instead of batches,
    // but ideally would like to do a batch submit once we can handle
    // some error cases like losing network connectivity
    switch (action) {
      case AnswerSaveActions[AnswerSaveActions.Submit]:
        this.submitAnswerAndQuit(Constants.ExitToDashboardAction);
        return;
      case AnswerSaveActions[AnswerSaveActions.Continue]:
        this.storeAnswers();
        this.nextQuestion();
        break;
      case AnswerSaveActions[AnswerSaveActions.SleepLibrary]:
        let sleep_library_action = Constants.ExitToSleepLibraryAction;

        if (this.useWebView) {
          if ([this.sleepConciergeSurveyId, this.airCurve11SleepinessSurveyId].includes(this.surveyData.surveyId)) {
            sleep_library_action = this.surveyAnswerCoachingLibraryActionMapper[this.selectedAnswer.AnswerId];
            if (this.selectedAnswer.AnswerId === "device") {
              sleep_library_action += `/${this.useYourDeviceAS11SleepLibraryId}`;
            }
            else if (["breath-in", "breath-out"].includes(this.selectedAnswer.AnswerId)) {
              sleep_library_action += `/${this.adjustRampTimeAS11SleepLibraryId}`;
            }
          }
        }
        this.submitAnswerAndQuit(sleep_library_action);
        break;
      case AnswerSaveActions[AnswerSaveActions.MaskCapture]:
        this.startAppImageCapture({flowId: this.maskSenseFlowId});
        break;
      case AnswerSaveActions[AnswerSaveActions.ArticleDisplay]:
        this.submitSurveyContentEvent();
        const sourceId = this.identity.generateSourceId();
        this.submitAnswerAndNavigateToNewRoute("article", {
          "locale": this.language,
          "articles": this.selectedAnswer?.Articles,
          "prevArticles": "",
          "deviceSerial": this.getDeviceSerial(),
          "patientId": this.surveyData.patientId,
          "origin": this.getMappedSurveyOrigin(),
          "sourceId": sourceId,
          "isArticleRatingFeatureEnabled": this.surveyData.isArticleRatingFeatureEnabled ?? false,
          "darkMode": this.darkMode
        })
        break;
      case AnswerSaveActions[AnswerSaveActions.MaskWizard]:
        this.submitAnswerAndQuit(Constants.ExitToMaskWizardAction);
        break;
      default:
        if(this.valid) {
          // no action specified, default is just submit and quit
          this.submitAnswerAndQuit(Constants.ExitToDashboardAction);
        }
        break;
    }
  }

  determineAction(currentQuestion): string {
    if (currentQuestion.Template === 'SurveyCompletedDialog') {
      return currentQuestion?.DefaultContinueAction ?? AnswerSaveActions.Submit;
    }

    // Check if an answer is selected and required
    if (!this.isAnswerSelected() && (currentQuestion.RequiresAnswer ?? true)) {
      this.valid = false;
      return null; // No action if answer is required but not selected
    }

    if (!this.isAnswerSelected() && (currentQuestion.RequiresAnswer ?? true) === false) {
      return currentQuestion?.Action ?? AnswerSaveActions.Submit;
    }

    // Set answer time
    currentQuestion.answerTime = new Date().toISOString();

    // Determine action based on selected answer or default
    let action = this.selectedAnswer?.Action ?? AnswerSaveActions.Continue;

    // Dynamically map action to submit if it's the last question
    if (action == AnswerSaveActions[AnswerSaveActions.Continue] && this.currentQuestion.isLastQuestion) {
      action = AnswerSaveActions.Submit;
    }

    return action;
  }

  handleQuestionNegativeAction(): void {
    let currentQuestion = this.surveyTemplate.Questions[this.currentQuestionIndex];
    if (!this.isAnswerSelected() && (currentQuestion.Dismissible ?? false) === false) {
      this.valid = false;
      return;
    }
    currentQuestion.answerTime = new Date().toISOString();
    this.submitAnswerAndQuit(Constants.ExitToDashboardAction);
  }

  submitAnswerAndNavigateToNewRoute(routeName: string, queryParams: any): void {
    this.spinner.show();
    let successCallback = () => {
      this.selectedAnswer = null;
      this.router.navigate(
        [routeName],
        {queryParams: queryParams}
      );
    };

    let failedCallback = () => {
      console.error("Failed to post survey.  Navigating to new route.");
      this.selectedAnswer = null;
      this.router.navigate(
        [routeName],
        {queryParams: queryParams}
      );
    };


    this.submitAnswer(null)
      .then(successCallback)
      .catch(failedCallback)
      .finally(() => {
        this.spinner.hide();
      })
  }

  submitAnswerAndQuit(redirectAction: string): void {
    this.spinner.show();
    let successCallback = () => {};
    let failedCallback = () => {};

    if (!isJavascriptInjectedIntoWebView()){
      successCallback = () => {
        this.selectedAnswer = null;
        // trigger to notify the app that the session is ended.
        window.location.href = redirectAction;
      };
      failedCallback = () => {
        window.location.href = redirectAction;
      };
    }

    this.submitAnswer(redirectAction)
      .then(successCallback)
      .catch(failedCallback);
  }

  sortRemainingQuestions(): void {
    const previousQuestions = this.surveyTemplate.Questions.slice(0, this.currentQuestionIndex + 1);
    let remainingQuestions = this.surveyTemplate.Questions.slice(this.currentQuestionIndex + 1, this.surveyTemplate.Questions.length);
    remainingQuestions.forEach(q => {
      if (!this.selectedAnswer.NextQuestions.includes(q.QuestionId)) this.selectedAnswer.NextQuestions.push(q.QuestionId)
    })
    let sortedRemainingQuestions = remainingQuestions.sort((a, b) => {
      return this.selectedAnswer.NextQuestions.indexOf(a.QuestionId) -
        this.selectedAnswer.NextQuestions.indexOf(b.QuestionId);
    });

    this.surveyTemplate.Questions = previousQuestions.concat(sortedRemainingQuestions);
    this.refreshVisibilityBasedOnResponseScope();
  }

  lookupNextVisibleQuestion(): void {
    let q = this.surveyTemplate.Questions[++this.currentQuestionIndex];
    while (q !== null && (q?.isVisible === false || this.checkScoring(q) < (q?.Scoring?.RequiredScore ?? 0)))
    {
      this.surveyTemplate.Questions[this.currentQuestionIndex].SelectedAnswer = {"AnswerId": "ineligible"};
      q = this.surveyTemplate.Questions[++this.currentQuestionIndex];
    }
    return q;
  }

  lookupPreviousVisibleQuestion(): void {
    if (this.currentQuestionIndex <= 0) {
      return this.currentQuestion;
    }

    let q = this.surveyTemplate.Questions[--this.currentQuestionIndex];
    while (q !== null && ((q?.isVisible === false && this.currentQuestionIndex > 0) || q.Template === "Dialog")) { // Skip Dialogs
      if (q.Template === "Dialog") {
        this.currentDialog = "FirstDialog";
      }
      q = this.surveyTemplate.Questions[--this.currentQuestionIndex];
    }

    return q;
  }

  checkScoring(question): number {
    if (question.Scoring === null || question.Scoring === undefined) {
      // No scoring defined, so no need to check.
      return 0;
    }
    let score: number = 0;
    let scorable: boolean = false;
    this.surveyTemplate.Questions.forEach(sq => {
      if ((question.Scoring?.Questions ?? []).includes(sq.QuestionId)) {
        score += sq.SelectedAnswer?.Score ?? 0;
        if (sq.SelectedAnswer?.Score !== null && sq.SelectedAnswer?.Score !== undefined) {
          scorable = true;
        }
      }
    })
    if (scorable) {
      return score;
    }
    else {
      return null;
    }
  }

  nextQuestion(): void {
    if (this.selectedAnswer?.hasOwnProperty("NextQuestions")) {
      this.sortRemainingQuestions();
    }
    this.currentQuestion = this.lookupNextVisibleQuestion();
    if (this.currentQuestion.Template === "Dialog") {
      this.showDialog = true;
      this.setupDialog();
      this.showHeaderInfoButton = this.currentQuestion[this.currentDialog].hasOwnProperty("InfoDialog") &&
                                  !this.currentQuestion[this.currentDialog].InfoDialog?.HideInHeader;
    if (this.currentQuestion?.WarningText) {
      // Translate by default doesn't accept the html property, we need to sanitize it.
      // We also need to tell JS to use the Angular reference version of the function
      this.translate.get(this.currentQuestion.WarningText).subscribe((translated: string) => {
        const replaced = translated.replace(
          /onclick=openDialogModal/g,
          'onclick="window.angularComponentReference.component.openDialogModal();"'
        );

        this.sanitizedWarningText = this.sanitizer.bypassSecurityTrustHtml(replaced);
      });
    }
      return;
    }
    this.showHeaderInfoButton = this.surveyTemplate.hasOwnProperty("InfoDialog");
    if (this.currentQuestion != null) {
      if (this.currentQuestion.hasOwnProperty("PossibleAnswers")){
        this.currentQuestion.PossibleAnswers = this.currentQuestion.PossibleAnswers.filter(p => !this.shouldHideOption(p));
      }
      if (this.currentQuestion.hasOwnProperty("AllowSkip")) {
        this.allowSkip = this.currentQuestion.AllowSkip;
      }
      else {
        this.allowSkip = false;
      }
      if (this.currentQuestion.Template === "SurveyCompletedDialog") {
        this.validationTextStringId = "";
      }
      else {
        this.validationTextStringId = this.currentQuestion.Template !== "BMI" ? "please-select-option" : "input-all-fields";
      }
      if (this.currentQuestion.hasOwnProperty("SelectedAnswer")){
        this.selectedAnswer = this.currentQuestion.SelectedAnswer;
        this.selectedTexts = this.getAnswerSelectedText(this.selectedAnswer);
      } else {
        this.selectedAnswer = null;
        this.selectedTexts = [];
      }
      this.updatePageText();
      this.submitCurrentSurveyState();
      if (this.reachTheLastVisibleQuestion()) {
        this.allowSkip = false;
      }
    }

    this.setProgressBar();
  }

  previousQuestion(): void {
    if (this.surveyTemplate?.Template === "Dialog") {
      this.currentDialog = this.dialogObj.BackButton?.NavigateTo;

      if (this.currentDialog != null) {
        this.setupDialog();
        return;
      }

    }
    this.storeAnswers();
    this.currentQuestion = this.lookupPreviousVisibleQuestion();
    this.validationTextStringId = this.currentQuestion.Template !== "BMI" ? "please-select-option" : "input-all-fields";
    this.continueText = this.currentQuestion.DefaultContinueText ?? this.defaultContinueText;
    this.cancelText = this.showCancelButton ? this.currentQuestion.DefaultCancelText: "";
    if (this.currentQuestion.hasOwnProperty("SelectedAnswer")){
      this.selectedAnswer = this.currentQuestion.SelectedAnswer;
      this.selectedTexts = this.getAnswerSelectedText(this.selectedAnswer);
    }

    if (this.currentQuestion.hasOwnProperty("AllowSkip")) {
      this.allowSkip = this.currentQuestion.AllowSkip;
    }
    else {
      this.allowSkip = false;
    }

    this.submitCurrentSurveyState();
    this.updatePageText();
    this.valid = true;
  }

  submitAnswer(action: string): Promise<any> {
    return this.submitPayload(action, this.generateSurveyResponse());
  }

  submitCodeRequest(): Promise<any> {
    return this.submitPayload(null, this.generateCodeRequestPayload());
  }

  submitPayload(action: string, payload: SurveyResponseWrapper): Promise<any> {
    // The new flow posts the survey response via injected javascript interface
    if (isJavascriptInjectedIntoWebView()){
      return postSurveyResponse(payload, action);
    }

    // The legacy flow posts the survey response via API Gateway
    return this.webInterface.saveAnswers(
      payload,
      this.surveyData.patientId,
      null,
      this.surveyData.surveyId)
      .toPromise().catch(err => this.handleException(err));
  }

  // send the all the questions answered so far to store them in the app without making the network call
  storeAnswers(): void {
    const surveyResponse = this.generateSurveyResponse();
    // Display event & answers are sent to the app for storage in the same call.
    // So keep answers locally here so they can be re-submitted with the display events
    this.savedResponses = surveyResponse.surveyResponse;

    if (isJavascriptInjectedIntoWebView()) {
      storeSurveyResponse(surveyResponse);
    }
  }

  submitDialogSurveyResponse(answer: string, action: string = null): Promise<any> {
    if (this.dialogObj?.QuestionId === "somnio-code") {
      // If the dialog is requesting a code, only submit the code request payload
      if (!this.submittedCodeRequest) {
        this.surveyTemplate.Questions[this.currentQuestionIndex].SelectedAnswer = {"AnswerId": answer};
        if (answer === "yes") {
          this.submittedCodeRequest = true;
          return this.submitCodeRequest();
        }
      } else {
        // If the dialog is requesting a code, but the user has already submitted it, just set the answer
        // to "yes" so the survey can continue.
        this.surveyTemplate.Questions[this.currentQuestionIndex].SelectedAnswer = {"AnswerId": "yes"};
      }
      return Promise.resolve(null);
    }

    const surveyResponse = this.generateDialogSurveyResponse(answer);

    if (isJavascriptInjectedIntoWebView()) {
      return postSurveyResponse(surveyResponse, action);
    }

    // The legacy flow posts the survey response via API Gateway
    return this.webInterface.saveAnswers(
      surveyResponse,
      this.surveyData.patientId,
      null,
      this.surveyData.surveyId)
      .toPromise().catch(err => this.handleException(err));
  }

  startAppImageCapture(flow: any): void {
    const surveyResponse = this.generateSurveyResponse();

    if (isJavascriptInjectedIntoWebView()) {
      storeSurveyResponse(surveyResponse);
      startImageCapture(flow);
    } else{
        startImageCapture(flow);
  }
  }

  buildSurveyAnswerMap(){
    const questions = {}
    // submit questions as a batch
    this.surveyTemplate.Questions.forEach(q => {
      const questionId = q.QuestionId;

      if (q?.SelectedAnswer !== null) {
        const answer = q.SelectedAnswer;
        if (answer?.ResponseType == ResponseType.MultiSelect)
        {
          questions[questionId] = new Set();
          answer.Answers?.forEach(a => {
            questions[questionId].add(a?.AnswerId);
          })
        } else if (answer?.hasOwnProperty("AnswerId")) {
            questions[questionId] = new Set([answer?.AnswerId]);
        }
      }
    })
    return questions;
  }

  refreshVisibilityBasedOnResponseScope() {
    // Go through survey and set hide/visible based on responses.
    const questions = this.buildSurveyAnswerMap();
    let lastVisibleQuestion = null;
    let firstVisibleQuestion = null;
    const surveyLocaleResult = this.checkLocaleScope(this.surveyTemplate?.scope);

    this.surveyTemplate.Questions.forEach(q => {
        let conditionMet = true;
        const localeResult = this.checkLocaleScope(q.scope);

        // if a question has a condition loop through each condition and evaluate the
        // condition based on answers already selected
        q.scope?.conditional?.conditions?.forEach(condition => {
            if (condition.type === "answer-selected" || condition.type === "answer-not-selected") {
                let hasAnyConditionedAnswer = false;

                if (Array.isArray(condition.properties?.answer)) {
                    condition.properties.answer.forEach(answer => {
                        if (questions[condition.properties?.questionId]?.has(answer)) {
                            hasAnyConditionedAnswer = true;
                        }
                    });
                } else {
                    if (questions[condition.properties?.questionId]?.has(condition.properties?.answer)) {
                        hasAnyConditionedAnswer = true;
                    }
                }

                if ((condition.type === "answer-selected" && !hasAnyConditionedAnswer) ||
                    (condition.type === "answer-not-selected" && hasAnyConditionedAnswer)) {
                    conditionMet = false;
                }
            }
        });

        q.isVisible = surveyLocaleResult && conditionMet && localeResult && !q?.Hidden;
        q.isLastQuestion = false;

        if (q.isVisible) {
            lastVisibleQuestion = q;
            if (!firstVisibleQuestion) {
                firstVisibleQuestion = q;
                q.isFirstQuestion = true;
            }
        }
    });

    if (lastVisibleQuestion) {
        lastVisibleQuestion.isLastQuestion = true;
    }
  }


  generateCodeRequestPayload(): SurveyResponseWrapper {
    const somnioQuestionIds = [
      "somnio-code",
      "how-long-to-fall-asleep-prior-to-therapy",
      "sleep-disturbed-prior-to-therapy",
      "wake-up-too-early-prior-to-therapy",
      "afraid-to-go-to-sleep-prior-to-therapy"
    ];
    const response = new SurveyResponse();
    response.surveyId = this.surveyData.surveyId;
    response.sourceId = this.identity.generateSourceId();
    response.questions = [];

    for (let questionId of somnioQuestionIds) {
      const question = this.surveyTemplate.Questions.find(q => q.QuestionId === questionId);
      if (question && question.SelectedAnswer) {
        let q = new SurveyQuestion();
        q.questionId = questionId;
        q.dayOffset = this.surveyData.dayOffset;
        q.source = new SurveySource(this.identity, this.getMappedSurveyOrigin());
        q.answers = [new SurveyEnumAnswer(question.SelectedAnswer.AnswerId)];
        q.declined = false;
        response.questions.push(q);
      }
    }
    return new SurveyResponseWrapper(response, null);
  }

  generateSurveyResponse(): SurveyResponseWrapper {
    let response = new SurveyResponse();
    response.surveyId = this.surveyData.surveyId;
    response.sourceId = this.identity.generateSourceId();

    if (this.surveyTemplate?.EngagementCard) {
      const question = new SurveyQuestion();
      question.questionId = this.surveyTemplate.EngagementCard.QuestionId;
      question.dayOffset = this.surveyData.dayOffset;
      question.source = new SurveySource(this.identity, this.getMappedSurveyOrigin());
      question.answers = [new SurveyEnumAnswer(this.surveyTemplate.EngagementCard.ProvidedAnswerId)];
      question.declined = false;
      response.questions = [question];
    }
    let surveyTotalScore: number = -1;
    // submit questions as a batch
    this.surveyTemplate.Questions?.forEach(q => {
        let isAnswerableQuestion = (!q.Template?.includes("Dialog")) || (q.QuestionId === "somnio-code");
        if (!isAnswerableQuestion || !((q.isVisible ?? false) || q.hasOwnProperty("SelectedAnswer") && q.SelectedAnswer !== null)) {
          return;
        }
        const question = new SurveyQuestion();
        question.questionId = q.QuestionId;
        question.dayOffset = this.surveyData.dayOffset;
        question.source = new SurveySource(this.identity, this.getMappedSurveyOrigin());

        if (q.SelectedAnswer?.hasOwnProperty("AnswerId") && q.SelectedAnswer.AnswerId === "ineligible") {
          if (q.QuestionId === "somnio-code") {
            if (this.submittedCodeRequest === true) {
              // if the patient submitted the code request, and then changed their answers, we need to set the answer to "yes"
              q.SelectedAnswer.AnswerId = "yes";
            } else {
              // If the patient was ineligible, we need to skip the question.
              return;
            }
          }else {
            // If the question is ineligible, we need to skip the question.
            return;
          }
        }

        if (q.answerTime !== null) {
          question.dateTime = q.answerTime;
        }

        // Datetime must be set on every question in a payload.
        // If it hasn't been set yet (such as in decline scenario),
        // set it to the current time.
        if (!question.hasOwnProperty("dateTime") || question.dateTime === null || question.dateTime === undefined) {
          question.dateTime = new Date().toISOString();
        }

        if (q.hasOwnProperty("SelectedAnswer") && q.SelectedAnswer !== null) {
          const answer = q.SelectedAnswer;
          if (answer.hasOwnProperty("Score") && answer.Score !== null && answer.Score !== undefined) {
            if (surveyTotalScore < 0) {
              // If this is the first score, set the total to 0.
              surveyTotalScore = 0;
            }
            surveyTotalScore += answer.Score;
          }
          if (answer.ResponseType === ResponseType.Bmi) {
            // BMI has specific handling to submit each unit as a different survey answer.
            response.questions = response.questions.concat(this.formatBmiResponse(answer));
          }
          else if (answer.ResponseType === ResponseType.MultiQuestionRadioGroup) {
            response.questions = response.questions.concat(this.formatRadioGroupResponse(answer));
          }
          else if (answer.ResponseType === ResponseType.MultiQuestionMultiGroup) {
            response.questions = response.questions.concat(this.formatMultiGroupResponse(answer, q.Questions));
          }
          else {
            switch (answer.ResponseType){
              case ResponseType.Bmi:
                break;
              case ResponseType.FreeText:
                question.answers.push(new SurveyEnumAnswer(answer.AnswerId));
                question.answers.push(new SurveyStringAnswer(answer.Description?.trim()));
                break;
              case ResponseType.MultiSelect:
                question.answers = this.formatMultiSelectResponses(answer.Answers);
                break;
              default:
                question.answers.push(new SurveyEnumAnswer(answer.AnswerId));
                break;
            }
          }
        }
        else if (question.answers?.length == 0 && q.hasOwnProperty("declined") && q.declined) {
            question.declined = true;
        }

        if (question.answers?.length > 0 || (question.declined?? false)) {
           response.questions.push(question);
        }
    });
    if (surveyTotalScore >= 0) {
      // add a new question to the response with the total score if there is no score-gated question.
      const scoreQuestion = new SurveyQuestion();
      scoreQuestion.questionId = "survey-score";
      scoreQuestion.dayOffset = this.surveyData.dayOffset;
      scoreQuestion.source = new SurveySource(this.identity, this.getMappedSurveyOrigin());
      scoreQuestion.answers.push(new SurveyNumberAnswer(surveyTotalScore));
      response.questions.push(scoreQuestion);
    }
    if (response.questions?.length === 0) {
      response = null;
    }
    return new SurveyResponseWrapper(response, this.surveyAnalyticsEvents);
  }

  /**
   * Handles formatting for multiselect repsonses when questions are grouped
   * @param selectedAnswers: The answers provided by the user
   * @param questions: All of the questions in the group
   * @returns The answers formatted for submission to SleepCon
   */
  formatMultiGroupResponse(selectedAnswers: any, questions: any[]): SurveyQuestion[] {
    const surveyQuestions: SurveyQuestion[] = [];
    let questionsAnswered = [];

    for (let key in selectedAnswers.Answers){
      const answer = selectedAnswers.Answers[key];
      const question = new SurveyQuestion(selectedAnswers.AnswerTime);
      question.questionId = key;
      question.dayOffset = this.surveyData.dayOffset;
      question.source = new SurveySource(this.identity, this.getMappedSurveyOrigin());
      answer.forEach(element => {
        element.Answers.forEach(ans => {
          question.answers.push(new SurveyEnumAnswer(ans.AnswerId));
        });
      });
      surveyQuestions.push(question);
      questionsAnswered.push(key);
    }
    // Add declined/skipped questions.
    questions.forEach( q => {
      if (!questionsAnswered.includes(q.QuestionId)) {
        surveyQuestions.push(this.generateDeclinedQuestion(q.QuestionId));
      }
    });

    return surveyQuestions;
  }

  /**
   * Handles formatting for multiselect responses
   * @param selectedAnswers: The answers provided by the user
   * @returns The answers formatted for submission to SleepCon
   */
  formatMultiSelectResponses(selectedAnswers: any[]): SurveyAnswer[] {
    const surveyAnswers: SurveyAnswer[] = [];

    selectedAnswers.forEach(answer => {
      if (answer.ResponseType === ResponseType.FreeText) {
        surveyAnswers.push(new SurveyEnumAnswer(answer.AnswerId));
        surveyAnswers.push(new SurveyStringAnswer(answer.Description?.trim()));
      } else {
        surveyAnswers.push(new SurveyEnumAnswer(answer.AnswerId));
      }
    });

    return surveyAnswers;
  }

  formatBmiResponse(bmiAnswer: any): SurveyQuestion[] {
    const bmiResponses: SurveyQuestion[] = [];
    const responseDateTime = new Date().toISOString();

    bmiResponses.push(this.generateBmiQuestion(responseDateTime, Bmi.WeightQuestionId, bmiAnswer.AnswerId.weight));
    bmiResponses.push(this.generateBmiQuestion(responseDateTime, Bmi.HeightQuestionId, bmiAnswer.AnswerId.height));
    bmiResponses.push(this.generateBmiQuestion(responseDateTime, Bmi.BmiResultQuestionId, bmiAnswer.AnswerId.bmi));
    return bmiResponses;
  }

  generateBmiQuestion(responseDateTime: string, questionId: string, answer: number): SurveyQuestion {
    const weightQuestion = new SurveyQuestion(responseDateTime);
    weightQuestion.questionId = questionId;
    weightQuestion.dayOffset = this.surveyData.dayOffset;
    weightQuestion.source = new SurveySource(this.identity, this.getMappedSurveyOrigin());
    weightQuestion.answers.push(new SurveyNumberAnswer(answer));

    return weightQuestion;
  }

  formatRadioGroupResponse(answer: any): SurveyQuestion[] {
    const radioGroupResponse: SurveyQuestion[] = [];
    const responseDateTime = new Date().toISOString();

    for (let key in answer.Answers){
      radioGroupResponse.push(this.generateRadioQuestion(responseDateTime, key, answer.Answers[key]?.AnswerId));
    }

    return radioGroupResponse;
  }

  generateRadioQuestion(responseDateTime: string, questionId: string, answer: string): SurveyQuestion {
    const radioQuestion = new SurveyQuestion(responseDateTime);
    radioQuestion.questionId = questionId;
    radioQuestion.dayOffset = this.surveyData.dayOffset;
    radioQuestion.source = new SurveySource(this.identity, this.getMappedSurveyOrigin());
    radioQuestion.answers.push(new SurveyEnumAnswer(answer));

    return radioQuestion;
  }

  /**
   * Redirect site to page not found to
   * handle error cases
   */
  redirectToNotFound(): void {
    // home page will display not found, as this app is only designed
    // to display specifc items based on the URL provided
    if (this.darkMode === true) {
      this.router.navigate(["/"], { queryParams: { darkMode: true } });
      return; // Prevent navigating twice
    }
    this.router.navigate(["/"]);
  }

  displayHeaderInfoModal(): void {

    let infoDialog = this.surveyTemplate.InfoDialog;

    if (infoDialog === undefined || infoDialog === null) {
      infoDialog = this.currentQuestion[this.currentDialog].InfoDialog;
    }

    const dialogConfig = {
      data: {dialogConfig: infoDialog}
    }

    if (infoDialog["Size"] == "large") {
      dialogConfig['height'] = '85%';
    }


    this.headerInfoDialog.open(InfoDialogComponent, dialogConfig)
  }

  /* tslint:disable:no-string-literal */
  getMicroCoachingStringId(answer: any): string {
    if (!answer) {
      return "";
    }

    const answerId = this.stripInvalidCharsFromAnswerId(answer.AnswerId);

    if (!this.surveyData.baselineResponse) {
      return this.getDefaultMicroCoachingId(answerId);
    }

    let stringId;

    try {
      const baselineResponseStripped = this.stripInvalidCharsFromAnswerId(this.surveyData.baselineResponse)
      stringId = this.microCoachingMapper[baselineResponseStripped][answerId];
    } catch (error) {
      stringId = this.getDefaultMicroCoachingId(answerId);
    }

    return stringId;
  }

  getDefaultMicroCoachingId(answerId: string): string {
    if (this.surveyData.surveyId === this.airCurve11SleepinessSurveyId) {
      return this.microCoachingMapper.defaultsleepquality[answerId];
    }
    return this.microCoachingMapper.default[answerId];
  }

  stripInvalidCharsFromAnswerId(answerId: string): string {
    if (!answerId) {
      return "";
    }

    return answerId.replace(/-/g, "");
  }

  setupSurveyQuestions(): void {
    if (this.surveyTemplate.hasOwnProperty("Questions")) {
      this.surveyTemplate.Questions = this.surveyTemplate.Questions.filter(q => !this.shouldHideQuestion(q));
      this.surveyTemplate.Questions[0].DefaultCancelText = "no-thanks";
      this.surveyTemplate.Questions[0].Dismissible = true;
      this.allowSkip = this.surveyTemplate.Questions[0].AllowSkip === true;
      this.currentQuestion = this.surveyTemplate.Questions[this.currentQuestionIndex];
      this.continueText = this.currentQuestion.DefaultContinueText ?? this.defaultContinueText;
      this.cancelText = this.showCancelButton ? this.getDefaultCancelText() : "";
      this.selectedAnswer = null;
      this.valid = true;
      this.footer = null;
      this.showSurveyQuestions = true;
      this.showBackButton = false; // first question do not display

      if (this.currentQuestion.hasOwnProperty("PossibleAnswers")) {
        this.currentQuestion.PossibleAnswers = this.currentQuestion.PossibleAnswers.filter(p => !this.shouldHideOption(p));
      }

      this.refreshVisibilityBasedOnResponseScope();
      this.submitCurrentSurveyState();
      if (this.getFirstVisibleQuestion() === undefined) {
        console.error(`Error: No questions are visible. Clearing app notification and quiting.`);
        this.clearAppSyncNotificationAndExit();
      }
    }
  }

  setupDialog(): void {
    if (this.showDialog) {
      this.dialogObj = this.surveyTemplate[this.currentDialog];
      if (this.dialogObj == null) {
        this.dialogObj = this.currentQuestion[this.currentDialog];
      }

      /** If this is a self help follow up card, retrieve any particular issue answer to determine correct description **/
      if (this.dialogObj?.QuestionId === this.selfHelpFollowUpQuestionId && this.currentDialog === "FirstDialog") {
        const anyParticularIssueResponse = this.surveyData.anyParticularIssueResponse;
        if (anyParticularIssueResponse in this.selfHelpFollowUpDecriptionMapper[this.surveyData.surveyId]){
          this.dialogObj.Description = this.selfHelpFollowUpDecriptionMapper[this.surveyData.surveyId][anyParticularIssueResponse];
        }
      }

      this.showHeaderInfoButton = this.dialogObj.hasOwnProperty("InfoDialog");
      // If the dialog description is a placeholder, replace it with the appropriate string id
      if (this.dialogObj.Description === "#REPAP-CONTACT#") {
        this.dialogObj.Description = this.getRepapStringId();
      }
      if (this.dialogObj.Description === "#REPAP-CONTACT-HOME#") {
        this.dialogObj.Description = this.getRepapStringId(true);
      }
      this.continueText = this.dialogObj.PositiveButton.Text;
      this.cancelText = this.dialogObj.NegativeButton?.Text || "";
      this.valid = true;
      this.footer = this.dialogObj.Footer;
      this.dialogObj.SplashScreen = this.surveyTemplate.SplashScreen;
      this.dialogObj.Template = this.surveyTemplate.Template;

      if (this.surveyData?.orgId != null){
        this.dialogObj.orgId = this.surveyData.orgId;
      }
      this.showBackButton = this.dialogObj.BackButton != null;
      this.submitDialogSurveyState();
  }
}
  // This function handles those on-flight survey invites with EngagementCard template, converting them to new Dialog template
  convertEngagmentCardToDialog(): void {
    if (this.surveyTemplate.hasOwnProperty("EngagementCard")) {
      this.surveyTemplate.FirstDialog = {
        "Title": this.surveyTemplate.EngagementCard.Title,
        "QuestionId": this.surveyTemplate.EngagementCard.Question.QuestionId,
        "Description": this.surveyTemplate.EngagementCard.Description,
        "PositiveButton": {
          "Text": "no",
          "AnswerId": this.surveyTemplate.EngagementCard.Question.NegativeAnswerId,
          "NavigateTo": "ThirdDialog"
        },
        "NegativeButton": {
          "Text": "yes",
          "AnswerId": this.surveyTemplate.EngagementCard.Question.PositiveAnswerId,
          "NavigateTo": "SecondDialog"
        }
      };
      this.surveyTemplate.SecondDialog = {
        "Title": this.surveyTemplate.NegativeDialog.Title,
        "Description": this.surveyTemplate.NegativeDialog.Description,
        "PositiveButton": {
          "Text": this.surveyTemplate.NegativeDialog.PositiveButton.Text,
          "NavigateTo": this.surveyTemplate.NegativeDialog.PositiveButton.NavigateTo
        }
      };
      this.surveyTemplate.ThirdDialog = {
        "Title": this.surveyTemplate.PositiveDialog.Title,
        "Description": this.surveyTemplate.PositiveDialog.Description,
        "PositiveButton": {
          "Text": this.surveyTemplate.PositiveDialog.PositiveButton.Text,
          "NavigateTo": this.surveyTemplate.PositiveDialog.PositiveButton.NavigateTo
        },
        "NegativeButton": {
          "Text": this.surveyTemplate.PositiveDialog.NegativeButton.Text,
          "NavigateTo": this.surveyTemplate.PositiveDialog.PositiveButton.NavigateTo
        }
      };
    }
  }

  resetButtonsText(): void {
    this.continueText = null;
    this.cancelText = null;
  }

  navigate(action: string): void {
    window.location.href = action;
  }

  /**
   * Opens an external link in the device's default browser and then dismisses
   * the in-app browser (WebView), submitting the dialog response first so the
   * survey notification is cleared before the user leaves.
   *
   * Inside the native WebView the answer is posted with the external URL as the
   * redirect action, so the native "surveyPosted" callback navigates to the
   * link only AFTER the response has been posted. Navigating immediately
   * (as on Android) races and cancels the in-flight post, leaving the survey
   * notification in the database so it re-prompts when the user returns.
   *
   * Outside the native WebView (where the close action and link interception
   * don't exist) we simply submit and navigate.
   */
  async openExternalLinkAfterDialogSubmit(answerId: string, url: string, submittable: boolean): Promise<void> {
    const inWebView = isJavascriptInjectedIntoWebView();

    if (inWebView) {
      // Register the WebView dismissal listeners before navigation begins.
      this.registerExternalBrowserCloseListeners();
    }

    if (submittable) {
      // Inside the WebView, pass the URL as the redirect action so the native
      // surveyPosted callback navigates only AFTER the response has been posted
      // (and the notification cleared). Outside the WebView the action is
      // ignored and we navigate ourselves below.
      await this.submitDialogSurveyResponse(answerId, inWebView ? url : null)
        .catch(err => this.handleException(err));
    }

    // In the WebView a submittable response triggers navigation via the native
    // callback; we navigate ourselves only when nothing else will.
    if (!inWebView || !submittable) {
      this.navigate(url);
    }
  }

  /**
   * Submits the dialog response and then navigates to the given action
   * (typically an exit/close action such as "#close-activity" or
   * ExitToDashboardAction).
   *
   * Inside the native WebView the answer is posted with the action as the
   * redirect, so the native "surveyPosted" callback navigates only AFTER the
   * response has been posted. Navigating immediately (as on Android) races and
   * cancels the in-flight post, leaving the survey notification in the database
   * so it re-prompts when the user returns. Outside the WebView the action is
   * ignored by the post, so we navigate ourselves after submitting.
   */
  async submitDialogAndNavigate(answerId: string, action: string, submittable: boolean): Promise<void> {
    const inWebView = isJavascriptInjectedIntoWebView();

    if (submittable) {
      await this.submitDialogSurveyResponse(answerId, inWebView ? action : null)
        .catch(err => this.handleException(err));
    }

    // In the WebView a submittable response triggers navigation via the native
    // callback; we navigate ourselves only when nothing else will.
    if (!inWebView || !submittable) {
      this.navigate(action);
    }
  }

  /**
   * Registers page lifecycle listeners that dismiss the in-app browser
   * (WebView) once an external link has been opened in the device's default
   * browser.
   *
   * We must NOT close the WebView on a fixed timer: doing so tears down the
   * activity/WebView while the OS is still handing off the navigation, which
   * cancels the external redirect. Instead we wait until the page is actually
   * backgrounded (the external browser has come to the foreground), which is
   * the signal that the redirect has definitively succeeded, and only then
   * trigger "#close-activity" to dismiss the WebView.
   *
   * As a fallback, if the close while backgrounded is ignored by the native
   * app, we retry the close when the user returns to the WebView (the page
   * becomes visible again after having been hidden).
   *
   * Only meaningful inside the native in-app WebView; callers must guard with
   * isJavascriptInjectedIntoWebView().
   */
  registerExternalBrowserCloseListeners(): void {
    let hidden = false;
    let finished = false;

    const triggerClose = () => {
      window.location.href = Constants.ExitToDashboardAction;
    };

    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        // The external browser has come to the foreground on top of the
        // WebView, i.e. the redirect has been handed off and succeeded.
        hidden = true;
        triggerClose();
      } else if (hidden && !finished) {
        // The user has returned from the external browser. Retry the close in
        // case the earlier attempt was ignored while backgrounded.
        finished = true;
        cleanup();
        triggerClose();
      }
    };

    const onPageHide = () => {
      // pagehide also fires for ordinary navigations/unloads; only treat it as
      // a background event (redirect handed off) when the page is actually
      // hidden, otherwise we could close the WebView mid-handoff.
      if (document.hidden) {
        hidden = true;
        triggerClose();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
  }

  // Use postSurveyResponse interface to handle the decline action
  handleSurveyDecline(action = null): Promise<any> {
    if (action !== null) {
      this.spinner.show();
    }
    const surveyResponse = new SurveyResponseWrapper(null, this.surveyAnalyticsEvents);
    if (isJavascriptInjectedIntoWebView()){
      return postSurveyResponse(surveyResponse, action);
    }
    return this.webInterface.saveAnswers(
      surveyResponse,
      this.surveyData.patientId,
      null,
      this.surveyData.surveyId)
      .toPromise().catch(err => this.handleException(err));
 }

  isQuestionDismissible(): boolean {
    return this.currentQuestion.hasOwnProperty("Dismissible");
  }

  skipSelected(): void {
    this.surveyTemplate.Questions[this.currentQuestionIndex].declined = true;
    this.storeAnswers();

    this.updatePageText();
    if (this.reachTheLastVisibleQuestion()) {
      // Skipping the last question will submit the survey, storing any
      // answered questions and marking others as declined.
      this.submitAnswerAndQuit(Constants.ExitToDashboardAction);
      return;
    }

    this.nextQuestion();
    this.selectedAnswer = null;
  }

  skipSection(numScreensToSkip): void {
    for(let i=0; i<numScreensToSkip; i++){
      this.skipSelected();
    }
  }

  getDefaultCancelText(): string {
    if (this.useWebView) {
      return this.currentQuestion.DefaultCancelText;
    }
    return null;
  }

  setProgressBar(): void {
    if (this.surveyTemplate !== null && this.currentQuestionIndex > -1) {
      const totalQuestions = this.surveyTemplate.Questions.filter(x => x.isVisible).length;
      const progress = ((this.currentQuestionIndex + 1) / totalQuestions * 100).toFixed(2);
      this.progressBarStyle = `width: ${progress}%`;
      this.ariaValueNow = progress.toString();
    }
  }

  shouldHideOption(possibleAnswer: any): boolean {
    return this.shouldHideOptionForExperiencedUsers(possibleAnswer) ||
      this.shouldHideBreathOption(possibleAnswer) || !this.checkLocaleScope(possibleAnswer.scope);
  }

  checkLocaleScope(scope: any): boolean {
    let localeResult = true;
    if (scope?.hasOwnProperty("locales") && scope.locales !== null) {
      let locales: string[] = scope.locales;
      if (!(locales.length == 0 || locales.indexOf("*") != -1)){
        // if locales are constrained in any way, check the list
        // against what the user provided.
        localeResult = false;
        locales.forEach(locale => {
          if (locale.toLowerCase() === this.language){
            localeResult = true;
          }
        })
      }
    }
    return localeResult;
  }

  shouldHideOptionForExperiencedUsers(possibleAnswer: any): boolean {
    return possibleAnswer.AnswerId === this.getUsedToTherapyAnswerId &&
      this.surveyData.dayOffset >= this.scheduleStartDateForExperiencedUsers;
  }

  shouldHideBreathOption(possibleAnswer: any): boolean {
    return this.breathOptions.includes(possibleAnswer.AnswerId) && this.surveyData.dayOffset > 3;
  }

  shouldHideQuestion(question: any): boolean {
    if (this.surveyData.surveyId === this.onboardingSurveyId && question.QuestionId === this.baselineQuestionId) {
      // always want to display this question in baseline survey
      return false;
    }

    return question.QuestionId === this.baselineQuestionId && (this.surveyData.baselineResponse !== undefined || this.surveyData.dayOffset !== 3);
  }

  reachTheLastVisibleQuestion(): boolean {
    return this.currentQuestion?.QuestionId === this.getLastVisibleQuestion()?.QuestionId;
  }

  handleSurveySkip(action = null): void {
    if (action !== null) {
      this.spinner.show();
    }

    if (isJavascriptInjectedIntoWebView()) {
      declineSurvey(new SurveyResponseWrapper(null, this.surveyAnalyticsEvents), action).then(() => {});
    }
    else {
      window.location.href = Constants.ExitToDashboardAction;
    }
  }

  resetSelection(): void {
    if (this.surveyTemplate.Questions.length > this.currentQuestionIndex + 1)
    {
      let remainingQuestions = this.surveyTemplate.Questions.slice(this.currentQuestionIndex+1, this.surveyTemplate.Questions.length);
      remainingQuestions.forEach(q => {
        // Use delete instead of setting to null.  This will cause hasOwnProperty to return false which
        // seems important when initializing some questions the first time.
        delete q.SelectedAnswer;

        // Delete nested answers (e.g. radio group answers)
        if (q.hasOwnProperty("Questions")) {
          q.Questions.forEach(q2 => {
            delete q2.SelectedAnswer;
          })
        }
      })
    }
    this.selectedAnswer = null;
  }

  generateDialogSurveyResponse(answer: string): SurveyResponseWrapper {
      const response = new SurveyResponse();
      response.surveyId = this.surveyData.surveyId;
      response.sourceId = this.identity.generateSourceId();
      const question = new SurveyQuestion();
      question.questionId = this.dialogObj.QuestionId;
      question.dayOffset = this.surveyData.dayOffset;
      question.source = new SurveySource(this.identity, this.getMappedSurveyOrigin());
      question.dateTime = new Date().toISOString();
      question.answers.push(new SurveyEnumAnswer(answer));
      response.questions.push(question);

      return new SurveyResponseWrapper(response, this.surveyAnalyticsEvents);
  }

  getSurveyDisplayEvent(questionId: string): SurveyDisplayEvent {
    const surveyDisplayEvent = new SurveyDisplayEvent();
    surveyDisplayEvent.inviteCode = this.invite;
    surveyDisplayEvent.surveyId = this.surveyData.surveyId;
    surveyDisplayEvent.sourceId = this.identity.generateSourceId();
    surveyDisplayEvent.patientId = this.surveyData.patientId;
    surveyDisplayEvent.deviceSerial = this.getDeviceSerial();
    surveyDisplayEvent.dayOffset = this.surveyData.dayOffset;
    surveyDisplayEvent.questionId = questionId;
    surveyDisplayEvent.locale = this.language;
    surveyDisplayEvent.origin = this.getMappedSurveyOrigin();
    return surveyDisplayEvent;
  }

  getSurveyContentEvent(contentId: string): SurveyContentEvent {
    const surveyContentEvent = new SurveyContentEvent();
    surveyContentEvent.surveyId = this.surveyData.surveyId;
    surveyContentEvent.sourceId = this.identity.generateSourceId();
    surveyContentEvent.patientId = this.surveyData.patientId;
    surveyContentEvent.deviceSerial = this.getDeviceSerial();
    surveyContentEvent.dayOffset = this.surveyData.dayOffset;
    surveyContentEvent.contentId = contentId;
    surveyContentEvent.locale = this.language;
    surveyContentEvent.origin = this.getMappedSurveyOrigin();
    return surveyContentEvent;
  }

  submitSurveyContentEvent(): void {
    if (this.selectedAnswer?.Articles?.length > 0) {
      this.selectedAnswer.Articles.forEach(article => {
        const articleDisplayEvent = this.getSurveyContentEvent(article);
        this.surveyAnalyticsEvents.push(articleDisplayEvent);
        if (isJavascriptInjectedIntoWebView()){
          return storeSurveyResponse(articleDisplayEvent);
        }
      });
    }
  }

  handleOpenContactEvent(event: string): Promise<any> {
    const contactEvent = this.getSurveyContentEvent(event);
    const surveyResponse = new SurveyResponseWrapper(null, [contactEvent]);

    if (isJavascriptInjectedIntoWebView()) {
      return postSurveyResponse(surveyResponse);
    }

    return this.webInterface.saveAnswers(
      surveyResponse,
      this.surveyData.patientId,
      null,
      this.surveyData.surveyId)
      .toPromise().catch(err => this.handleException(err));
  }

  /*
   * Submits the current state of the survey to the app to keep
   * in case the user X's out of the webview.
  */
  submitCurrentSurveyState(): void {
    // Add Survey Display Event for any sub-questions in a group.
    if (this.currentQuestion.hasOwnProperty("Questions") && this.currentQuestion.Questions !== null) {
      this.currentQuestion.Questions.forEach(q => {
        this.surveyAnalyticsEvents.push(this.getSurveyDisplayEvent(q.QuestionId));
        this.savedResponses?.questions?.push(this.generateDeclinedQuestion(q.QuestionId));
      });
    }
    else {
      const surveyDisplayEvent = this.getSurveyDisplayEvent(this.currentQuestion.QuestionId);
      this.surveyAnalyticsEvents.push(surveyDisplayEvent);
      this.savedResponses?.questions?.push(this.generateDeclinedQuestion(this.currentQuestion.QuestionId));
    }
    if (isJavascriptInjectedIntoWebView()){
      return storeSurveyResponse(new SurveyResponseWrapper(this.savedResponses, this.surveyAnalyticsEvents));
    }
  }

  /*
   * Submits the current state of survey templated dialog (e.g self help follow up and Repap survey)
   * to the app to keep in case the user X's out of the webview.
  */
  submitDialogSurveyState(): void {
    this.surveyAnalyticsEvents = this.dialogObj.QuestionId != null ? [this.getSurveyDisplayEvent(this.dialogObj.QuestionId)] : [];

    if (isJavascriptInjectedIntoWebView()){
      return storeSurveyResponse(new SurveyResponseWrapper(null, this.surveyAnalyticsEvents));
    }
  }

  generateDeclinedQuestion(questionId: string): SurveyQuestion {
    const question = new SurveyQuestion(new Date().toISOString());
    question.questionId = questionId;
    question.source = new SurveySource(this.identity, this.getMappedSurveyOrigin());
    question.dayOffset = this.surveyData.dayOffset;
    question.declined = true;

    return question;
  }

  handleException(err): void {
    console.log(`Error occurred when submitting a request. Error details: ${JSON.stringify(err)}`);
  }

  /*
   * Try to get the device serial from the app, and fall back to
   * survey invite metadata.
   * Note: The fallback is for testing, the app should always add the serial
   * number in case it has changed since the invite was generated.
  */
  getDeviceSerial(): string {
    return this.serial ?? this.surveyData.deviceSerial;
  }

  imageCaptureCompleted(event: CustomEvent): void {
    const flowId = event.detail.flowId;
    if (!this.imageCaptureFlowIds.includes(flowId)) {
      console.warn(`Unknown flow ID: ${flowId}`);
    }
    if (flowId === this.maskSenseFlowId) {
      this.saveMaskImage(event.detail.imageUrl);
    }
    this.selectedAnswer = null;
    this.nextQuestion();
  }

  imageCaptureInterrupted(event: CustomEvent): void {
    if (!this.imageCaptureFlowIds.includes(event.detail.flowId)) {
      console.warn(`Unknown flow ID: ${event.detail.flowId}`);
    }
    this.submitAnswerAndQuit(Constants.ExitToDashboardAction);
  }

  saveMaskImage(imageUrl: string): void {
    this.surveyTemplate.Questions.forEach(q => {
      if(q.QuestionId === "image-mask-model-number")
      {
        q.SelectedAnswer = {
          ResponseType : ResponseType.FreeText,
          AnswerId : "image-mask-model-number",
          Description : imageUrl,
        }
      }
    });
  }

  isAnswerSelected(): boolean {
    if (this.selectedAnswer === null) {
      return false;
    }
    else if (this.selectedAnswer.Answers !== undefined && this.currentQuestion.Questions) {
      return Object.keys(this.selectedAnswer.Answers).length === this.currentQuestion.Questions.length;
    }
    else if (this.selectedAnswer.Answers != null) {
      return this.selectedAnswer.Answers.length > 0;
    }
    else {
      return true;
    }
  }

  getMappedSurveyOrigin(): string {
     return this.surveyMethod === "myair-card" ? this.surveyMethod : this.usePush ? "myair-push" : "myair";
  }

  backSelected(): void {
    this.previousQuestion();
  }

  // Dynamically map the repap string id based on the group key
  getRepapStringId(isHomePage: boolean = false): string {
    let repapStringId = null;
    if (this.surveyData?.groups?.length > 0) {
      let matchingKey = this.surveyData.groups.find((key) => key in this.repapGroupStringIdMapping);
      repapStringId = matchingKey ? this.repapGroupStringIdMapping[matchingKey] : undefined;
    }
    if (!repapStringId) {
      repapStringId = `repap-v1-${this.surveyData.orgId}-info`;
    }
    if (isHomePage) {
      repapStringId = `${repapStringId}-home`;
    }
    return repapStringId;
  }
}
